// Ensure FoxTheme is available before running
if (!window.FoxTheme || !FoxTheme.settings || !FoxTheme.routes) {
    console.error('SKU Quick Order: FoxTheme not initialized');
} else {
    const SKU_PAGE_LIMIT = 250;
    let SKU_CACHE_KEY = null;
    let SKU_PAGE_URL = null; // Will be assigned at runtime from the section element
    const strings = FoxTheme?.skuQuickOrderStrings || {};

    // Function to cache the SKU Map
    async function buildAndCacheSkuMap() {
        if (!SKU_CACHE_KEY) {
            throw new Error('SKU Quick Order: Cache key not initialized.');
        }
        const cacheKey = SKU_CACHE_KEY;

        // Always fetch first page to check current product count
        const firstRes = await fetch(`${SKU_PAGE_URL}&page=1`);
        if (!firstRes.ok) {
            throw new Error(`Failed to fetch page 1: ${firstRes.status}`);
        }

        const firstData = await firstRes.json();
        const totalProducts = firstData.total_products;

        // Check existing cache
        const cachedRaw = sessionStorage.getItem(cacheKey);
        if (cachedRaw) {
            try {
                const cached = JSON.parse(cachedRaw);
                const isProductCountValid = cached.productCount === totalProducts;
                const isDataPresent = !!cached.data;
                
                if (isProductCountValid && isDataPresent) {
                    console.log('SKU Quick Order — Loaded SKU map from session cache');
                    return cached.data;
                }            
                console.log('SKU Quick Order — Cache invalid, rebuilding SKU map');
            } catch (err) {
                console.warn('SKU Quick Order — Cache corrupted, rebuilding SKU map');
            }
        }

        // Build full SKU map (including remaining pages)
        const skuMap = await buildFullSkuMap(firstData);

        // Store versioned cache
        sessionStorage.setItem(cacheKey, JSON.stringify({
            productCount: totalProducts,
            data: skuMap
        }));

        return skuMap;
    }

    // Function to build the full SKU map (including remaining pages)
    async function buildFullSkuMap(firstPageData) {
        const limit = SKU_PAGE_LIMIT;
        const totalProducts = firstPageData.total_products;
        const totalPages = Math.ceil(totalProducts / limit);

        let finalSkuMap = { ...firstPageData.sku_map };

        if (totalPages > 1) {
            const fetchPromises = [];

            for (let page = 2; page <= totalPages; page++) {
                fetchPromises.push(
                    fetch(`${SKU_PAGE_URL}&page=${page}`)
                        .then(res => {
                            if (!res.ok) {
                                throw new Error(`Failed page ${page}`);
                            }
                            return res.json();
                        })
                );
            }

            const remainingPages = await Promise.all(fetchPromises);

            remainingPages.forEach(pageData => {
                finalSkuMap = { ...finalSkuMap, ...pageData.sku_map };
            });
        }
        return finalSkuMap;
    }

    // Function to match SKUs to variants
    function matchSkusToVariants(skuList, skuToVariantMap) {
        const skusToAdd = [];
        const skusNotFound = [];
        const skusUnavailable = [];

        skuList.forEach(sku => {
            const skuMatch = skuToVariantMap[sku];
            
            if (!skuMatch) {
                skusNotFound.push(sku);
            } else if (!skuMatch.available) {
                skusUnavailable.push(sku);
            } else {
                skusToAdd.push({ sku, variantId: skuMatch.id });
            }
        });
        return { skusToAdd, skusNotFound, skusUnavailable };
    }

    // Function to add items to the cart page (if cart type is page)
    async function addItemsToCartPage(items) {
        const response = await fetch(FoxTheme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.description || `Cart request failed: ${response.status}`);
        }

        const cartJson = await (
            await fetch(FoxTheme.routes.cart_url, {
                ...FoxTheme.utils.fetchConfig('json', 'GET')
            })
        ).json();

        FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            { cart: cartJson }
        );
    }

    // Function to add items to the cart drawer (if cart type is drawer)
    // Using the same approach as the native product form submission
    async function addItemsToCartDrawer(items, { openDrawer = true } = {}) {
        const formData = new FormData();

        items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
        });

        // Let theme bundle sections
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(FoxTheme.routes.cart_add_url, config);
        if (!response.ok) {
            throw new Error(`Cart request failed: ${response.status}`);
        }
        const parsedState = await response.json();

        if (parsedState.status || parsedState.errors) {
            throw new Error(parsedState.description || parsedState.errors || 'Cart error');
        }

        // Fetch updated cart JSON
        const cartJson = await (
            await fetch(FoxTheme.routes.cart_url, {
                ...FoxTheme.utils.fetchConfig('json', 'GET')
            })
        ).json();

        cartJson['sections'] = parsedState['sections'];

        // Publish update so theme reacts properly
        FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            { cart: cartJson }
        );

        // Only open drawer if explicitly allowed
        if (openDrawer) {
            document.dispatchEvent(
                new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: { open: true }
                })
            );
        }
    }

    // Function to add items to the cart (either page or drawer)
    async function addItemsToCart(items, { openDrawer = true } = {}) {
        if (FoxTheme.settings.cartType === 'page') {
            await addItemsToCartPage(items);
            return;
        }
        await addItemsToCartDrawer(items, { openDrawer });
    }

    function renderMessages(container, {
        successCount = 0,
        notFound = [],
        unavailable = [],
        duplicateSkus = [],
        exceededLimit = false,
        totalEntered = 0,
        showViewCart = false,
        apiError = false,
        buttonStyle = 'btn--primary'
    }) {
        const blocks = [];

        // API Error
        if (apiError) {
            blocks.push(`
                <div class="form-error alert alert--error blocks-radius">
                    <h3 class="form-message form-message--error h6 m-0" role="alert" tabindex="-1">
                        ${strings.generalError}
                    </h3>
                </div>
            `);
        }

        // Success
        if (successCount > 0) {
            const addedMessage = strings.itemsAddedPartial
                .replace('[count]', successCount);

            blocks.push(`
                <div class="form-status-list" role="status">
                    <h3 class="form-message alert alert--success h6 blocks-radius" tabindex="-1">
                        ${addedMessage}
                    </h3>
                </div>
            `);
        }

        // Warnings (limit, invalid, unavailable)
        const warningMessages = [];

        if (exceededLimit) {
            warningMessages.push(
                strings.limitExceeded.replace('[total]', totalEntered)
            );
        }

        if (duplicateSkus.length > 0) {
            warningMessages.push(
                strings.duplicateSkus.replace('[skus]', duplicateSkus.join(', '))
            );
        }

        if (notFound.length > 0) {
            warningMessages.push(
                strings.invalidSkus.replace('[skus]', notFound.join(', '))
            );
        }

        if (unavailable.length > 0) {
            warningMessages.push(
                strings.unavailableSkus.replace('[skus]', unavailable.join(', '))
            );
        }

        if (warningMessages.length > 0) {
            blocks.push(`
                <div class="form-error alert alert--warning blocks-radius" role="alert">
                    <h3 class="form-message form-message--warning h6 m-0" role="alert" tabindex="-1">
                        ${warningMessages.join('<br>')}
                    </h3>
                </div>
            `);
        }

        // View Cart Button (theme button style)
        if (showViewCart) {
            blocks.push(`
                <div class="form-status-list">
                    <button type="button" class="btn ${buttonStyle} sku-view-cart-btn">
                        <span class="btn__text">
                            ${strings.viewCart}
                        </span>
                    </button>
                </div>
            `);
        }

        container.innerHTML = blocks.join('');
    }

    // Function to handle the quick order form submission
    async function handleQuickOrderSubmit(event, { form, skuMap }) {
        event.preventDefault();

        const submitBtn = form.querySelector('button[type="submit"]');
        const messageContainer = form.querySelector('.sku-quick-order__messages');
        const buttonStyle = form.dataset.buttonStyle || 'btn--primary';
        const skuInput = form.querySelector('textarea[name="sku_list"]');
        const resetSubmitButton = () => {
            submitBtn.disabled = false;
            submitBtn.classList.remove('btn--loading');
        };

        if (submitBtn.disabled) return;

        submitBtn.disabled = true;
        submitBtn.classList.add('btn--loading');
        messageContainer.innerHTML = '';

        // Check if SKU map is loaded
        if (!skuMap) {
            renderMessages(messageContainer, { apiError: true });
            resetSubmitButton();
            return;
        }

        // Get all SKUs from the input
        const rawSkuList = skuInput.value
            .split(',')
            .map(sku => sku.trim().toUpperCase())
            .filter(Boolean);

        // Enforce max SKU limit on the raw input
        const limitedRawSkuList = rawSkuList.slice(0, 50);
        const skusExceeded = rawSkuList.length > 50;

        // Track processed SKUs to avoid duplicates
        const processedSkus = new Set();
        const duplicateSkus = new Set();

        limitedRawSkuList.forEach((sku) => {
            if (processedSkus.has(sku)) {
                duplicateSkus.add(sku);
            } else {
                processedSkus.add(sku);
            }
        });

        const uniqueSkuList = [...processedSkus];
        const duplicateSkuList = [...duplicateSkus];

        // Unique SKUs that will actually be processed
        const allSkus = uniqueSkuList;

        // Empty input
        if (allSkus.length === 0) {
            messageContainer.innerHTML = `
                <div class="form-error alert alert--error blocks-radius" role="alert">
                    <h3 class="form-message form-message--error h6 m-0" tabindex="-1">
                        ${strings.emptyInput}
                    </h3>
                </div>
            `;

            resetSubmitButton();
            return;
        }

        const { skusToAdd, skusNotFound, skusUnavailable } = matchSkusToVariants(allSkus, skuMap);
        const successCount = skusToAdd.length;

        const hasWarnings =
            skusNotFound.length > 0 ||
            skusUnavailable.length > 0 ||
            duplicateSkuList.length > 0 ||
            skusExceeded;

        // No valid SKUs
        if (successCount === 0) {
            renderMessages(messageContainer, {
                notFound: skusNotFound,
                unavailable: skusUnavailable,
                duplicateSkus: duplicateSkuList,
                exceededLimit: skusExceeded,
                totalEntered: rawSkuList.length
            });

            resetSubmitButton();
            return;
        }

        const cartItems = skusToAdd.map(item => ({
            id: item.variantId,
            quantity: 1
        }));

        try {
            // SUCCESS HANDLING (Unified)
            const isDrawer = FoxTheme.settings.cartType === 'drawer';
            const shouldOpenDrawer = isDrawer && !hasWarnings;
            
            await addItemsToCart(cartItems, { openDrawer: shouldOpenDrawer });

            // Always show success message
            renderMessages(messageContainer, {
                successCount,
                notFound: skusNotFound,
                unavailable: skusUnavailable,
                duplicateSkus: duplicateSkuList,
                exceededLimit: skusExceeded,
                totalEntered: rawSkuList.length,
                showViewCart: (!isDrawer) || (isDrawer && hasWarnings),
                buttonStyle
            });

            // Clear input only if full success (no warnings)
            if (!hasWarnings) {
                skuInput.value = '';
            }

            resetSubmitButton();

        } catch (error) {
            console.error('Cart API error:', error);
            renderMessages(messageContainer, { apiError: true });
            resetSubmitButton();
        }
    }

    // Function to handle the view cart button click

    function handleViewCartClick(event) {
        if (!event.target.closest('.sku-view-cart-btn')) return;

        if (FoxTheme.settings.cartType === 'page') {
            window.location.href = FoxTheme.routes.cart_url;
        } else {
            document.dispatchEvent(
                new CustomEvent('cart:refresh', {
                    bubbles: true,
                    detail: { open: true }
                })
            );
        }
    }

    // Function to initialize the SKU quick order form
    document.addEventListener('DOMContentLoaded', async () => {
        const form = document.querySelector('.sku-quick-order-form');
        if (!form) return;

        const submitBtn = form.querySelector('button[type="submit"]');
        const messageContainer = form.querySelector('.sku-quick-order__messages');

        const sectionEl = form.closest('[data-sku-endpoint]');
        if (!sectionEl) {
            console.error('SKU Quick Order: No data endpoint element found.');
            return;
        }

        SKU_PAGE_URL = sectionEl.dataset.skuEndpoint;    
        SKU_CACHE_KEY = `skuQuickOrder_skuMap::${SKU_PAGE_URL}`; // Make cache unique per selected page

        if (!SKU_PAGE_URL) {
            console.error('SKU Quick Order: No data endpoint configured.');
            return;
        }

        let skuMap = null;    

        // Disable button until SKU map loads
        submitBtn.disabled = true;
        submitBtn.classList.add('btn--loading');

        messageContainer.innerHTML = `
            <div class="form-status-list" role="status">
                <h3 class="form-message h6 m-0" tabindex="-1">
                    ${strings.preparingForm}
                </h3>
            </div>
        `;

        try {
            skuMap = await buildAndCacheSkuMap();
            submitBtn.disabled = false;
            submitBtn.classList.remove('btn--loading');
            messageContainer.innerHTML = '';
        } catch (error) {
            console.error('Failed to build SKU map:', error);
            submitBtn.classList.remove('btn--loading');
            messageContainer.innerHTML = `
                <div class="form-error alert alert--error blocks-radius" role="alert">
                    <h3 class="form-message form-message--error h6 m-0" tabindex="-1">
                        ${strings.generalError}
                    </h3>
                </div>
            `;
            return;
        }

        // Submit handler
        form.addEventListener('submit', (event) =>
            handleQuickOrderSubmit(event, { form, skuMap })
        );

        // View cart click handler
        document.addEventListener('click', handleViewCartClick);
    });
}
